import { UseFormReturn } from 'react-hook-form'
import create from 'zustand'

import {
  ForecastModalFilterValues,
  ForecastModalHiringsTableValues,
  ForecastModalNewHiring,
  ForecastModalState,
  IForecastModalEmployeeTableForm,
  filterForecastModalEmployees,
  transformForecastModalData,
  IForecastModalData,
  setForecastModalEmployeeErrors,
  checkForecastModalEmployeeChanges,
  ForecastModalEmployeeTableFields,
  checkForecastModalEmployeeRowChange,
} from '.'
import { Country, PyramidForecastWithFiltersDto, PyramidForecastWithFiltersQuery } from '../../../types'
import { ForeacastModalTabs } from './types'

const initialValues = {
  pyramidForecastResponse: [],
  employeeData: [],
  newHiringsData: [],
  employeeImmutableData: [],
  employeeTableFormValues: {},
  filterEmployees: [],
  filterBu: [],
  filterEmployeeLevels: [],
  isModalOpened: false,
  activeTab: ForeacastModalTabs.Employees,
  filterValues: {},
  employeesSelectedRows: [],
  hiringsSelectedRows: [],
  hiringsTableForm: {} as UseFormReturn<ForecastModalHiringsTableValues>,
  deletedHirings: [],
  employeeForecastHasChanges: false,
  careerPath: null,
  options: {
    companyId: '',
    country: '' as Country,
  },
}

export const useForecastModalStore = create<ForecastModalState>((set) => ({
  ...initialValues,

  resetStore: () =>
    set((state) => {
      return { ...initialValues, options: state.options }
    }),

  setModalData: (data: PyramidForecastWithFiltersQuery) =>
    set(() => {
      const tData: IForecastModalData = transformForecastModalData(data.pyramidForecastWithFilters as PyramidForecastWithFiltersDto)
      return {
        pyramidForecastResponse: tData.pyramidForecastResponse,
        employeeData: tData.employeeData,
        employeeImmutableData: tData.employeeData,
        newHiringsData: tData.newHiringsData,
        employeeTableFormValues: tData.employeeTableFormValues,
        filterEmployees: tData.filterEmployees,
        filterBu: tData.filterBu,
        filterEmployeeLevels: data.employeeLevels,
        careerPath: tData.careerPath,
      }
    }),

  setEmployeeTableFormValues: (id, name, value) =>
    set((state) => {
      if (state.employeeTableFormValues[id].currentValue[name] !== value) {
        const employeeTableFormValues = {
          ...state.employeeTableFormValues,
          [id]: {
            pastValue: { ...state.employeeTableFormValues[id].pastValue, [name]: state.employeeTableFormValues[id].currentValue[name] },
            currentValue: { ...state.employeeTableFormValues[id].currentValue, [name]: value },
            lastModifiedField: name,
            hasErrors: setForecastModalEmployeeErrors(state.employeeTableFormValues[id].currentValue, name, value || ''),
          },
        }
        return {
          employeeTableFormValues,
          employeeForecastHasChanges: !!state.employeesSelectedRows.length
            ? checkForecastModalEmployeeChanges(state.employeeImmutableData, employeeTableFormValues, state.employeesSelectedRows)
            : state.employeeForecastHasChanges,
        }
      } else {
        return { ...state }
      }
    }),

  undoFormValues: (id) =>
    set((state) => {
      const employeeData = state.employeeImmutableData.filter((employee) => employee.employeeId === id)[0]
      const employeeTableFormValues = {
        ...state.employeeTableFormValues,
        [id]: {
          pastValue: { ...state.employeeTableFormValues[id].currentValue },
          currentValue: {
            forecastDate: employeeData.forecastDate,
            levelChange: employeeData.levelChange,
            forecastEndDate: employeeData.forecastEndDate,
          },
          lastModifiedField: '' as ForecastModalEmployeeTableFields,
          hasErrors: '' as ForecastModalEmployeeTableFields,
        },
      }
      return {
        employeeTableFormValues,
        employeeForecastHasChanges: !!state.employeesSelectedRows.length
          ? checkForecastModalEmployeeChanges(state.employeeImmutableData, employeeTableFormValues, state.employeesSelectedRows)
          : state.employeeForecastHasChanges,
      }
    }),

  deleteEmployeesForecast: () =>
    set((state) => {
      const _employeeTableFormValues = Object.keys(state.employeeTableFormValues)
        .filter(
          (key) =>
            state.employeesSelectedRows.includes(key) &&
            checkForecastModalEmployeeRowChange(key, state.employeeImmutableData, state.employeeTableFormValues),
        )
        .reduce((acc: IForecastModalEmployeeTableForm, current) => {
          acc[current] = {
            pastValue: { forecastDate: null, levelChange: null, forecastEndDate: null },
            currentValue: { forecastDate: null, levelChange: null, forecastEndDate: null },
            lastModifiedField: '',
            hasErrors: '',
          }
          return acc
        }, {})
      return { employeeTableFormValues: { ...state.employeeTableFormValues, ..._employeeTableFormValues }, employeesSelectedRows: [] }
    }),

  toggleModal: (isModalOpened: boolean) => set({ isModalOpened }),

  toggleActiveTab: (activeTab: ForeacastModalTabs) => set({ activeTab }),

  setFilterValues: (filterValues: ForecastModalFilterValues) =>
    set((state) => {
      return { employeeData: filterForecastModalEmployees(state.employeeImmutableData, filterValues), filterValues }
    }),

  deleteFilter: (filterKey: keyof ForecastModalFilterValues) =>
    set((state) => {
      const { [filterKey]: _, ...rest } = state.filterValues
      return { filterValues: rest, employeeData: filterForecastModalEmployees(state.employeeImmutableData, rest) }
    }),

  setEmployeeSelectedRows: (selectedRows: string[]) =>
    set((state) => {
      return {
        employeesSelectedRows: selectedRows,
        employeeForecastHasChanges: checkForecastModalEmployeeChanges(
          state.employeeImmutableData,
          state.employeeTableFormValues,
          selectedRows,
        ),
      }
    }),

  setHiringsSelectedRows: (selectedRows: ForecastModalNewHiring[]) => set({ hiringsSelectedRows: selectedRows }),

  setHiringsTableForm: (hiringsTableForm: UseFormReturn<ForecastModalHiringsTableValues>) => set({ hiringsTableForm }),

  setMultipleHiringsForDeletion: (ids: string[]) =>
    set((state) => {
      return { deletedHirings: [...state.deletedHirings, ...ids] }
    }),

  setOptions: ({ companyId, country }: { companyId: string; country: Country }) => set({ options: { companyId, country } }),
}))
